/*
 * Copyright (c) 2025 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { RouteId } from 'router/routes';
import { _ } from '../../data/translation/i18n';
import { ScreenPermit } from '../../accessibility/permits';
import { AppAbility } from '../../accessibility/ability';

export enum NavBarType {
    MONITORING,
    SETTINGS,
    ADMIN,
}

export interface NavigationItem {
    routeId: RouteId;
    label: string;
    icon: string;
    permit: ScreenPermit;
    navBarType: NavBarType;
    nested?: NavigationItem[];
}

//contains only items that can be navigated by NavBar
const appNavigationItemsConfig: NavigationItem[] = [
    {
        routeId: RouteId.SystemSettingsInfo,
        label: _('domain.info'),
        icon: 'info',
        permit: 'USER_SETTINGS_INFO_SCREEN',
        navBarType: NavBarType.SETTINGS,
    },
    {
        routeId: RouteId.SystemSettingsSecurity,
        label: _('domain.security'),
        icon: 'lock',
        permit: 'USER_SETTINGS_SECURITY_SCREEN',
        navBarType: NavBarType.SETTINGS,
    },
    {
        routeId: RouteId.SystemAdminUsers,
        label: _('domain.users'),
        icon: 'people',
        permit: 'ADMIN_USERS_LIST_SCREEN',
        navBarType: NavBarType.ADMIN,
    },
    {
        routeId: RouteId.Home,
        label: _('domain.home'),
        icon: 'home',
        permit: 'INSPECTOR_HOME_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.HomeOperator,
        label: _('domain.home'),
        icon: 'home',
        permit: 'OPERATOR_HOME_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.HomeTaxpayer,
        label: _('domain.home'),
        icon: 'home',
        permit: 'TAXPAYER_HOME_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Infographic,
        label: _('domain.infographic'),
        icon: 'pie_chart',
        permit: 'INFOGRAPHICS_WRAPPER',
        navBarType: NavBarType.MONITORING,
        nested: [],
    },
    {
        routeId: RouteId.InfographicFin,
        label: _('domain.financial'),
        icon: 'attach_money',
        permit: 'INFOGRAPHICS_FIN_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.InfographicTech,
        label: _('domain.technical'),
        icon: 'build',
        permit: 'INFOGRAPHICS_TECH_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.InfographicCentral,
        label: _('domain.infographic'),
        icon: 'pie_chart',
        permit: 'CENTRAL_INSPECTOR_INFOGRAPHICS_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.InfographicRegional,
        label: _('domain.infographic'),
        icon: 'pie_chart',
        permit: 'REGIONAL_INSPECTOR_INFOGRAPHICS_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Statistics,
        label: _('domain.statistics'),
        icon: 'assessment',
        permit: 'STATISTICS_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.StatisticsCentral,
        label: _('domain.statistics'),
        icon: 'assessment',
        permit: 'CENTRAL_INSPECTOR_STATISTICS_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Alerts,
        label: _('domain.alerts'),
        icon: 'notification_important',
        permit: 'ALERTS_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Reports,
        label: _('domain.reports'),
        icon: 'dashboard',
        permit: 'REPORTS_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Map,
        label: _('domain.map'),
        icon: 'add_location',
        permit: 'MAPS_WRAPPER',
        navBarType: NavBarType.MONITORING,
        nested: [],
    },
    {
        routeId: RouteId.MapStores,
        label: _('domain.stores'),
        icon: 'apartment',
        permit: 'STORES_MAP_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.MapCashboxes,
        label: _('domain.cashboxes'),
        icon: 'point_of_sale',
        permit: 'CASHBOXES_MAP_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Registry,
        label: _('domain.registry'),
        icon: 'subject',
        permit: 'REGISTRY_WRAPPER',
        navBarType: NavBarType.MONITORING,
        nested: [],
    },
    {
        routeId: RouteId.RegistryOperators,
        label: _('domain.operators'),
        icon: 'group',
        permit: 'OPERATORS_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryCompanies,
        label: _('domain.companies'),
        icon: 'monetization_on_sharp',
        permit: 'COMPANIES_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryStores,
        label: _('domain.stores'),
        icon: 'apartment',
        permit: 'STORES_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryCashboxes,
        label: _('domain.cashboxes'),
        icon: 'point_of_sale',
        permit: 'CASHBOXES_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryDocuments,
        label: _('domain.documents'),
        icon: 'receipt',
        permit: 'DOCUMENTS_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryDevices,
        label: _('domain.devices'),
        icon: 'memory',
        permit: 'DEVICES_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.RegistryCashRegisters,
        label: _('domain.cashRegisters'),
        icon: 'devices_other',
        permit: 'CASHREGISTER_MODELS_LIST_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        routeId: RouteId.Service,
        label: _('domain.service'),
        icon: 'miscellaneous_services',
        permit: 'SERVICE_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
    {
        //to be placed in the very bottom of NavBar
        routeId: RouteId.Main,
        label: _('domain.searchDocument'),
        icon: 'search',
        permit: 'DOCUMENT_PICTURE_SCREEN',
        navBarType: NavBarType.MONITORING,
    },
];

export const getDefaultMonitoringRouteId = (ability: AppAbility): RouteId => {
    if (!ability || ability.rules.length === 0) return null;

    let routeId = null;
    appNavigationItemsConfig.some((item) => {
        const isConditionMet =
            item.navBarType === NavBarType.MONITORING &&
            item.permit !== 'DOCUMENT_PICTURE_SCREEN' &&
            ability.can('viewScreen', item.permit);
        if (isConditionMet) routeId = item.routeId;
        return isConditionMet;
    });
    return routeId;
};

export const getNavigationItemsConfig = (navBarType: NavBarType, ability: AppAbility): NavigationItem[] => {
    if (!ability || ability.rules.length === 0) return [];

    const navItems = appNavigationItemsConfig.filter((item) => {
        return item.navBarType === navBarType && ability.can('viewScreen', item.permit);
    });

    if (navBarType === NavBarType.MONITORING) {
        const nestedIndexes: number[] = [];

        let wrapperIndex: number = undefined;
        const isToWrapInphographicItems: boolean = navItems.some((item, index) => {
            wrapperIndex = index;
            return item.permit === 'INFOGRAPHICS_WRAPPER';
        });
        if (isToWrapInphographicItems) {
            const nestedItems = navItems.filter((item, index) => {
                const isNested = item.permit !== 'INFOGRAPHICS_WRAPPER' && item.routeId.includes(RouteId.Infographic);
                if (isNested) nestedIndexes.push(index);
                return isNested;
            });
            navItems[wrapperIndex].nested = nestedItems;
        }

        wrapperIndex = undefined;
        const isToWrapMapItems: boolean = navItems.some((item, index) => {
            wrapperIndex = index;
            return item.permit === 'MAPS_WRAPPER';
        });
        if (isToWrapMapItems) {
            const nestedItems = navItems.filter((item, index) => {
                const isNested = item.permit !== 'MAPS_WRAPPER' && item.routeId.includes(RouteId.Map);
                if (isNested) nestedIndexes.push(index);
                return isNested;
            });
            navItems[wrapperIndex].nested = nestedItems;
        }

        wrapperIndex = undefined;
        const isToWrapRegistryItems: boolean = navItems.some((item, index) => {
            wrapperIndex = index;
            return item.permit === 'REGISTRY_WRAPPER';
        });
        if (isToWrapRegistryItems) {
            const nestedItems = navItems.filter((item, index) => {
                const isNested = item.permit !== 'REGISTRY_WRAPPER' && item.routeId.includes(RouteId.Registry);
                if (isNested) nestedIndexes.push(index);
                return isNested;
            });
            navItems[wrapperIndex].nested = nestedItems;
        }
        if (nestedIndexes.length > 0) {
            return navItems.filter((_item, index) => {
                return !nestedIndexes.includes(index);
            });
        }
    }
    return navItems;
};
